<template>
  <div
    class="h-full flex relative"
    @mouseover="isHoveredOver = true"
    @mouseleave="isHoveredOver = false"
  >
    <div
      ref="editorContainer"
      class="w-full h-full"
      :class="{
        'bg-light-neutral-300': attributeStructure.isSynthesisFinalized,
      }"
      @click="showLockWarning"
    >
      <Editor
        v-if="isVisible"
        v-model:html="synthesis"
        :is-expanded="false"
        :provider="review.dataExtractionProvider"
        :user-name="userName"
        :user-color="color"
        :disabled="
          review.isReviewReadonly.value ||
          attributeStructure.isSynthesisFinalized
        "
        :field="`review-${review.entity.value.id}-synthesis-${attributeStructure.id}`"
        :placeholder="`Enter the synthesis of ${attributeStructure.label}`"
        class="h-full flex min-h-0"
        editor-classes=" p-3 hover:overflow-auto overflow-hidden pb-10 min-h-100 h-full w-full"
      />
      <div v-if="isHoveredOver" class="absolute top-20 -right-4 items-center">
        <div v-if="!attributeStructure.isSynthesisFinalized" class="space-y-2">
          <button
            :disabled="loading"
            class="rounded-lg bg-green-200 text-green-800 w-8 h-8 flex items-center justify-center"
            @click="finalizeSynthesis"
          >
            <HumanVerificationIcon class="fill-green-200 w-5 h-5" />
          </button>

          <button
            v-tooltip.top="
              !isAssistantConfigured
                ? 'The AI model is not configured. Please click to configure it in settings.'
                : null
            "
            :disabled="isLoading"
            class="rounded-lg w-8 h-8 flex items-center justify-center bg-purple-600 text-white"
            @click="handleClick"
          >
            <SparklesIcon
              class="w-5 h-5"
              :class="{
                'fill-white': !isGeneratingAnswer,
                'animate-fill-gradient': isGeneratingAnswer,
              }"
            />
          </button>
        </div>
        <div v-else class="">
          <button
            :disabled="loading"
            class="rounded-lg bg-primary-500 text-white w-8 h-8 flex items-center justify-center"
            @click.stop="unfinalizeSynthesis"
          >
            <PencilIcon class="w-5 h-5 fill-white" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from '@app/utils/injectStrict'
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { AuthKey, EvidenceServiceApiKey } from '@app/injectionKeys'
import { PencilIcon, SparklesIcon } from '@heroicons/vue/24/outline'
import useAiAssistant from '@app/composables/useAiAssistant'
import { useRouter } from 'vue-router'
import HumanVerificationIcon from '@app/components/Icons/HumanVerificationIcon.vue'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { Role } from '@core/domain/types/role.type'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import { AttributeStructure } from '@core/domain/models/data-extraction-plan-attribute'
import { ReviewKey } from '@app/views/Review/use-review'
import Editor from '../components/Editor.vue'
import { HttpException } from '@core/exceptions/http.exception'

const evidenceServiceApi = injectStrict(EvidenceServiceApiKey)
const { isConfigured: isAssistantConfigured } = useAiAssistant()
const router = useRouter()

const props = defineProps<{
  attributeStructure: AttributeStructure
}>()
const synthesis = ref('')
const review = injectStrict(ReviewKey)
const { user, color } = injectStrict(AuthKey)
const isHoveredOver = ref(false)
const snackbar = useSnackbar()

function showLockWarning() {
  if (!review.isReviewReadonly.value) return
  const isAdmin = user.value?.role === Role.ADMIN
  let message = ''
  if (
    review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED &&
    !review.isLocked.value
  ) {
    message = isAdmin
      ? 'The plan is unlocked, lock it to make changes.'
      : 'The plan is unlocked. Please contact an admin to make changes.'
  } else if (review.isLocked.value) {
    message = isAdmin
      ? 'The review is locked, unlock it to make changes.'
      : 'The review is locked. Please contact an admin to make changes.'
  }
  if (message) {
    snackbar.show(SnackbarState.ERROR, message)
  }
}

const userName = computed(
  () => user.value?.firstName + ' ' + user.value?.lastName,
)

const isGeneratingAnswer = ref(false)
const { isLoading, start: startLoading, stop: stopLoading } = useLoading()
const loading = computed(() => isLoading.value || isGeneratingAnswer.value)

async function synthesizeAttribute(attributeId: string) {
  isGeneratingAnswer.value = true
  startLoading()
  try {
    const { data } =
      await evidenceServiceApi.api.reviewsControllerSynthesizeDataExtractionAttribute(
        review.entity.value.id,
        {
          reviewId: review.entity.value.id,
          attributeId: attributeId,
        },
      )
    synthesis.value = data
  } catch (e: any) {
    const error = e as HttpException
    if (
      error?.response?.data?.statusCode >= 400 &&
      error?.response?.data?.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(SnackbarState.ERROR, 'An error occured, please try again.')
    }
    throw e
  } finally {
    isGeneratingAnswer.value = false
    stopLoading()
  }
}

async function finalizeSynthesis() {
  if (props.attributeStructure.isSynthesisFinalized) return

  startLoading()
  try {
    await review.finalizeAttributeSynthesis(props.attributeStructure.id)
  } finally {
    stopLoading()
  }
}

async function unfinalizeSynthesis() {
  if (!props.attributeStructure.isSynthesisFinalized) return

  startLoading()
  try {
    await review.unfinalizeAttributeSynthesis(props.attributeStructure.id)
  } finally {
    stopLoading()
  }
}

const isVisible = ref(false)
const editorContainer = useTemplateRef('editorContainer')

function handleClick() {
  if (isAssistantConfigured.value) {
    synthesizeAttribute(props.attributeStructure.id)
  } else {
    router.push('/settings')
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true
        observer.disconnect()
      }
    },
    {
      rootMargin: '50px',
      threshold: 0.1,
    },
  )

  if (editorContainer.value) {
    observer.observe(editorContainer.value)
  }

  onUnmounted(() => {
    observer.disconnect()
  })
})
</script>
